import type Handsontable from 'handsontable';

/**
 * 行/列拉伸轴配置
 * 隐藏行头/列头后，HOT 自带 ManualRowResize/ManualColumnResize 的原生交互
 * （强绑在行头/列头 TH 上）无法触发。这里把行/列差异点收敛到一张表，
 * 一套 handler 参数化跑两条对称路径；
 * 所有样式工厂只「返回样式对象」不直接改 DOM，避免 no-param-reassign，
 * 调用方拿到对象后用 Object.assign 应用到 element.style。
 */

export const MIN_ROW_HEIGHT = 24;
export const MIN_COL_WIDTH = 60;
export const RESIZE_EDGE_THRESHOLD = 5;
/** hover 握把（grip）厚度：长度等于单元格边线长 */
export const GRIP_THICK = 4;
/** 拖拽贯穿线（guide）厚度 */
export const GUIDE_THICK = 2;

export type TResizeAxis = 'row' | 'col';

/** 只挑握把/线条会用到的 CSS 属性，避免接全 CSSStyleDeclaration */
export type THandleStyle = Partial<Pick<CSSStyleDeclaration, 'display' | 'top' | 'left' | 'width' | 'height'>>;

export interface IResizeAxisConfig {
  pluginName: 'manualRowResize' | 'manualColumnResize';
  min: number;
  /** 读取拖拽起点所在元素的 DOMRect（行轴取 tr、列轴取 row=0 的 td） */
  getStartRect: (hot: Handsontable, index: number) => DOMRect | null;
  /** 从 rect 抽尺寸（行轴 height、列轴 width） */
  getStartSize: (rect: DOMRect) => number;
  /** 从 MouseEvent 计算相对起点的位移（行轴 y 差、列轴 x 差） */
  getDelta: (e: MouseEvent, start: { x: number; y: number; }) => number;
  /** hover 时握把样式（短粗方块，居中于单元格边线） */
  gripStyle: (rect: DOMRect, rootRect: DOMRect, rootEl: HTMLElement) => THandleStyle;
  /** 拖拽起始 guide 样式（贯穿整行/整列的细蓝线） */
  guideStartStyle: (startRect: DOMRect, rootRect: DOMRect, rootEl: HTMLElement) => THandleStyle;
  /** 拖拽中 guide 位置更新（只改轴向坐标，避免跨轴抖动） */
  guideMovingStyle: (startRect: DOMRect, newSize: number, rootRect: DOMRect) => THandleStyle;
}

export const RESIZE_AXIS_CONFIG: Record<TResizeAxis, IResizeAxisConfig> = {
  row: {
    pluginName: 'manualRowResize',
    min: MIN_ROW_HEIGHT,
    getStartRect: (hot, index) => {
      const cell = hot.getCell(index, 0) as HTMLElement | null;
      const tr = cell?.closest('tr') as HTMLElement | null;
      return tr ? tr.getBoundingClientRect() : null;
    },
    getStartSize: (rect) => rect.height,
    getDelta: (e, start) => e.clientY - start.y,
    gripStyle: (rect, rootRect) => ({
      display: 'block',
      top: `${rect.bottom - rootRect.top - GRIP_THICK / 2}px`,
      left: `${rect.left - rootRect.left}px`,
      width: `${rect.width}px`,
      height: `${GRIP_THICK}px`,
    }),
    guideStartStyle: (startRect, rootRect, rootEl) => ({
      display: 'block',
      top: `${startRect.bottom - rootRect.top - GUIDE_THICK / 2}px`,
      left: '0px',
      width: `${rootEl.clientWidth}px`,
      height: `${GUIDE_THICK}px`,
    }),
    guideMovingStyle: (startRect, newSize, rootRect) => ({
      top: `${startRect.top + newSize - rootRect.top - GUIDE_THICK / 2}px`,
    }),
  },
  col: {
    pluginName: 'manualColumnResize',
    min: MIN_COL_WIDTH,
    getStartRect: (hot, index) => {
      const td = hot.getCell(0, index) as HTMLElement | null;
      return td ? td.getBoundingClientRect() : null;
    },
    getStartSize: (rect) => rect.width,
    getDelta: (e, start) => e.clientX - start.x,
    gripStyle: (rect, rootRect) => ({
      display: 'block',
      left: `${rect.right - rootRect.left - GRIP_THICK / 2}px`,
      top: `${rect.top - rootRect.top}px`,
      width: `${GRIP_THICK}px`,
      height: `${rect.height}px`,
    }),
    guideStartStyle: (startRect, rootRect, rootEl) => ({
      display: 'block',
      left: `${startRect.right - rootRect.left - GUIDE_THICK / 2}px`,
      top: '0px',
      width: `${GUIDE_THICK}px`,
      height: `${rootEl.clientHeight}px`,
    }),
    guideMovingStyle: (startRect, newSize, rootRect) => ({
      left: `${startRect.left + newSize - rootRect.left - GUIDE_THICK / 2}px`,
    }),
  },
};
